import * as types from "types";
import type { UploadProgress } from "contexts/UploadContext/UploadContext";
import { uploadFile as uploadToFileApi } from "services/fileApi/methods/uploadFile";
import validateFile from "./validateFile";

/**
 * Helper to extract an error message from an unknown variable.
 * Replaced:
 *   const errorMessage = error instanceof Error
 *     ? error.message : (error as { message?: string })?.message ?? "Upload failed";
 * TODO: figure out what shapes `error` can actually be.
 */
function getErrorMessage(error: unknown, defaultErrorMsg: string): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  return defaultErrorMsg;
}

interface uploadFileOpts {
  /**
   * The file being uploaded.
   */
  file: File;
  /**
   * The ID of the chat thread this file is associated with.
   */
  chatId: types.ChatThread["id"];
  setUploadProgress: (upload: UploadProgress) => void;
  removeUploadProgress: (upload: { chatId: string; fileName: string }) => void;
  getAccessToken: () => Promise<string>;
}

/**
 * Uploads a single file.
 * If an upload fails, the failure will be recorded with a call to
 * setUploadProgress() and this function will return `null`.
 * If an upload succeeds, this function will return a File Message.
 */
async function uploadFile({
  file,
  chatId,
  setUploadProgress,
  removeUploadProgress,
  getAccessToken,
}: uploadFileOpts): Promise<null | types.FileMessage> {
  const [isValid, validationErrMsg] = validateFile(file);
  if (isValid === false) {
    setUploadProgress({
      chatId,
      fileName: file.name,
      status: "error",
      errorMessage: validationErrMsg,
    });
    return null;
  }

  // Callback function to log progress during file upload.
  // Note: ProgressEvent is assumed to be emitted by XMLHttpRequest.upload.onprogress
  const onProgress = (progressEvent: ProgressEvent) => {
    // If `lengthComputable` is `false`, then `progressEvent.total` has no meaning
    // and thus the progress percentage cannot be computed.
    // https://developer.mozilla.org/en-US/docs/Web/API/ProgressEvent/lengthComputable
    if (progressEvent.lengthComputable === false) return;

    const totalUploadSize = progressEvent.total;
    const amountUploadedSoFar = progressEvent.loaded;
    const percentCompleted = Math.round(
      (amountUploadedSoFar * 100) / totalUploadSize
    );

    setUploadProgress({
      chatId,
      fileName: file.name,
      status: "uploading",
      percentCompleted,
    });
  };

  try {
    const response = await uploadToFileApi(
      file,
      chatId,
      onProgress,
      getAccessToken
    );
    removeUploadProgress({ fileName: file.name, chatId });
    return {
      uuid: crypto.randomUUID(),
      role: "file",
      content: response.filename,
      meta: {
        file_id: response.file_id,
        variants: response.variants.map((v) => ({
          variant_id: v.variant_id,
          variant_type: v.variant_type,
        })),
        filename: response.filename,
      },
    };
  } catch (error) {
    console.error("Error uploading file: ", error);
    const errorMessage = getErrorMessage(error, "Upload failed");
    setUploadProgress({
      chatId,
      fileName: file.name,
      status: "error",
      errorMessage,
    });
    return null;
  }
}

export interface UploadFilesOpts {
  files: File[];
  chatId: types.ChatThread["id"];
  setUploadProgress: (upload: UploadProgress) => void;
  removeUploadProgress: (upload: { chatId: string; fileName: string }) => void;
  getAccessToken: () => Promise<string>;
}

/**
 * Upload a batch of files for a chat thread and return a `FileMessage` for
 * each file that succeeded. Per-file failures are surfaced through the
 * upload-progress UI (`setUploadProgress({ status: "error" })`),
 */
export async function uploadFiles({
  files,
  chatId,
  setUploadProgress,
  removeUploadProgress,
  getAccessToken,
}: UploadFilesOpts): Promise<types.FileMessage[]> {
  // Upload all the files.
  const results = await Promise.all(
    files.map((file) =>
      uploadFile({
        file,
        chatId,
        setUploadProgress,
        removeUploadProgress,
        getAccessToken,
      })
    )
  );

  // Return File Messages for each file that uploaded successfully.
  return results.filter((r) => r !== null);
}
